import { addDays, startOfDay, startOfWeek } from "@app/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSettingsStore, useTasksStore } from "../../stores/index.js";
import { useCalendarNavStore } from "../../stores/calendarNav.store.js";
import { useViewFilters } from "../../lib/useViewFilters.js";
import { useExpandedWindow } from "../../features/calendar-view/useExpandedWindow.js";
import { multiDayFocusAnchor } from "../../features/calendar-view/multi-day-focus.js";
import { useMultiDayFocusRollover } from "../../features/calendar-view/useMultiDayFocusRollover.js";
import { WeekGrid } from "../../features/calendar-view/WeekGrid.js";
import { CalendarHeader } from "../../features/calendar-view/CalendarHeader.js";
import { InboxPanel, InboxToggle } from "../../features/inbox/index.js";
import { MobileInboxDrawer } from "../../features/mobile/index.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useCalendarNav, useRememberedCalendarAnchor } from "./useCalendarNav.js";

const WEEK_DAYS = 7;

export function WeekRoute() {
  const tasks = useTasksStore((s) => s.tasks);
  const rules = useTasksStore((s) => s.rules);
  const overrides = useTasksStore((s) => s.overrides);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const applyFilters = useViewFilters();
  const weekStartsOn = useSettingsStore((s) => s.weekStartsOn);
  const multiDayFocus = useSettingsStore((s) => s.multiDayFocus);
  const setDayCount = useSettingsStore((s) => s.setCalendarDayCount);
  const navigate = useNavigate();
  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);

  const normalizeWeekStart = useCallback(
    (d: Date) => multiDayFocus === "current_week"
      ? startOfDay(startOfWeek(d, { weekStartsOn }))
      : startOfDay(d),
    [multiDayFocus, weekStartsOn],
  );
  const defaultWeekStart = useCallback(
    () => multiDayFocusAnchor(new Date(), multiDayFocus, weekStartsOn),
    [multiDayFocus, weekStartsOn],
  );
  const [weekStart, setWeekStart, shiftWeekStartRaw] = useRememberedCalendarAnchor(
    "week",
    defaultWeekStart,
    normalizeWeekStart,
  );
  useMultiDayFocusRollover(setWeekStart, multiDayFocus, weekStartsOn);
  const [inboxOpen, setInboxOpen] = useState(false);
  const mobile = useMobileUx();

  const handleAnchorChange = useCallback(
    (d: Date) => setWeekStart(d),
    [setWeekStart],
  );
  useCalendarNav("week", handleAnchorChange);

  const setStep = useCalendarNavStore((s) => s.setStep);
  useEffect(() => {
    setStep((delta) => setWeekStart((prev) => addDays(prev, delta * 7)));
    return () => setStep(null);
  }, [setStep]);

  // Day-granular drag/swipe moves one day at a time WITHOUT re-snapping to the
  // week boundary (the raw shifter) — so the week can pan day-by-day in both
  // directions. The prev/next nav + Today still use the normalized setter.
  function handleDayShift(delta: number) {
    shiftWeekStartRaw((prev) => addDays(prev, delta));
  }

  function handleDayCountChange(delta: number) {
    void setDayCount(WEEK_DAYS + delta);
    navigate("/calendar/days");
  }

  const filtered = useMemo(() => applyFilters(tasks), [applyFilters, tasks]);
  const { days, windowStart } = useExpandedWindow(weekStart, WEEK_DAYS, filtered, rules, overrides);

  return (
    <div className={`flex h-full min-h-0 flex-col ${mobile ? "" : "gap-4"}`}>
      <CalendarHeader
        anchor={weekStart}
        onChange={handleAnchorChange}
        onToday={() => setWeekStart(multiDayFocusAnchor(new Date(), multiDayFocus, weekStartsOn))}
        unit="week"
        actions={<InboxToggle open={inboxOpen} onToggle={() => setInboxOpen((v) => !v)} />}
      />
      <div className="flex min-h-0 flex-1 gap-4">
        <div className="min-w-0 flex-1"><WeekGrid days={days} windowStart={windowStart} visibleStart={weekStart} visibleCount={WEEK_DAYS} onDayShift={handleDayShift} onDayCountChange={handleDayCountChange} /></div>
        {inboxOpen && !mobile && (
          <aside className="w-64 shrink-0 rounded-md border bg-card p-2">
            <InboxPanel />
          </aside>
        )}
      </div>
      {mobile && <MobileInboxDrawer open={inboxOpen} onOpenChange={setInboxOpen} />}
    </div>
  );
}
